import { store } from '@/store';
import {
    CustomersController,
    ICustomersGetResponse,
    ICustomersInsuranceManualResponse,
    ICustomersInsuranceTestClaim,
    ICustomersInsuranceTestClaimPrescription,
    ICustomersTestInsuranceFillResponse,
    ICustomersTestInsuranceResponse,
    ICustomersUserOnboardingResponse
} from '../endpoints/customers';
import { ITransferPrescriptions } from '../types/interfaces/transfer-prescriptions';
import { IProvider } from '../types/interfaces/provider';
import _ from 'lodash';

export const getPreviousTestClaim = (insurance: ICustomersInsuranceManualResponse, customer?: ICustomersGetResponse) => {
    const previousTestClaims = (customer || store.getState().onboarding.user)?.userOnboarding?.details?.insuranceTestClaims || [];
    return previousTestClaims.find(claim => claim.insuranceId === insurance.id);
};

const getEmptyTestClaim = (insurance: ICustomersInsuranceManualResponse): ICustomersInsuranceTestClaim => ({
    insuranceId: insurance.id,
    prescriptions: []
});

const mapFillForMetadata = (
    fill: ICustomersTestInsuranceFillResponse,
    prescription: ITransferPrescriptions
): ICustomersInsuranceTestClaimPrescription => ({
    ndc: fill.ndc,
    medid: +prescription.medid,
    coPay: fill.coPay,
    coverageType: fill.insurances[0].coverageType,
    responseStatus: fill.insurances[0].responseStatus,
    rejectCodes: fill.insurances[0].rejectCodes, // reject codes are reason for reject
    additionalMessage: fill.insurances[0].additionalMessage
});

const filterAlreadyTestedPrescriptions = (claim: ICustomersInsuranceTestClaim, allList: ITransferPrescriptions[]) => {
    const previouslyCheckedMedIdList = new Set(claim.prescriptions.map(item => item.medid));
    const remainList = allList.filter(item => !previouslyCheckedMedIdList.has(+item.medid));

    return remainList;
};

const runSingleTestClaim = async (
    insurance: ICustomersInsuranceManualResponse,
    provider: IProvider,
    prescriptionList: ITransferPrescriptions[]
) => {
    const requestModel = {
        id: insurance.id,
        npi: +provider.npi,
        spi: +provider.spi,
        testClaimBillings: prescriptionList.map(item => ({
            ndc: 'placeholder',
            medid: +item.medid,
            quantity: 30,
            daysSupply: 30
        }))
    };

    const result = await store.dispatch(CustomersController.testInsurance(requestModel));
    // if (result.meta.requestStatus !== 'fulfilled') return null as never;
    if (result.meta.requestStatus !== 'fulfilled') {
        return prescriptionList.map(item => ({
            medid: item.medid,
            error: 'Server error'
        }));
    }

    const payload = result.payload as ICustomersTestInsuranceResponse;

    return payload.patient.fills.map((fill, fillIndex) => {
        return mapFillForMetadata(fill, prescriptionList[fillIndex]);
    });
};

const runTestClaimByProvider = async (insurance: ICustomersInsuranceManualResponse, prescriptionList: ITransferPrescriptions[]) => {
    const groupedByProvider = _.groupBy(prescriptionList, item => `${item.provider.npi}_${item.provider.spi}`);

    const requests = Object.values(groupedByProvider).map(listByProvider => {
        const { provider } = listByProvider[0];
        return runSingleTestClaim(insurance, provider, listByProvider);
    });

    const results = await Promise.allSettled(requests);
    const [fulfilled] = _.partition(results, item => item.status === 'fulfilled');
    return fulfilled.flatMap(item => item.value);
};

export const updateTestClaimMetadataModel = (
    userOnboarding: ICustomersUserOnboardingResponse,
    claims: ICustomersInsuranceTestClaim[],
    additionalMetaData: Partial<ICustomersUserOnboardingResponse> = {}
): ICustomersUserOnboardingResponse => {
    const oldClaims = (userOnboarding.details!.insuranceTestClaims || []).filter(test => {
        const isOld = claims.every(claim => claim.insuranceId !== test.insuranceId);
        return isOld;
    });

    return {
        ...userOnboarding,
        details: {
            ...userOnboarding.details!,
            insuranceTestClaims: [...oldClaims, ...claims],
            ...additionalMetaData
        }
    };
};

export const testClaim = async (
    insurance: ICustomersInsuranceManualResponse,
    prescriptions: ITransferPrescriptions[],
    customer?: ICustomersGetResponse
) => {
    let claim = getPreviousTestClaim(insurance, customer) ?? getEmptyTestClaim(insurance);
    // filter the error ndc cases so that we can get fresh requests for those... thus re running the test
    claim = { ...claim, prescriptions: claim.prescriptions.filter(prescription => !prescription.error) };

    const newPrescriptions = filterAlreadyTestedPrescriptions(claim, prescriptions);
    const newTestResults = await runTestClaimByProvider(insurance, newPrescriptions);

    const prescriptionFills = [
        ...claim.prescriptions,
        ...newTestResults,
    ];

    // Consumer should care about updating metadata on its own
    // If hasUpdate flag is true
    return {
        hasUpdate: !!newTestResults.length,
        result: {
            ...claim,
            prescriptions: prescriptionFills
        } as ICustomersInsuranceTestClaim
    };
};

export const findPrevTestClaimWithOnlyErrors = (insurance: ICustomersInsuranceManualResponse, user: ICustomersGetResponse) => {
    const previousTestClaims = (user || store.getState().onboarding.user)?.userOnboarding?.details?.insuranceTestClaims || [];
    return previousTestClaims.find(
        claim => claim.insuranceId === insurance.id && claim.prescriptions.every(prescription => !!prescription.error)
    );
};